import { arrayMove } from '@dnd-kit/sortable';
import {
  BTCategoryResponse,
  BTMenuItemResponse,
  BTMenuResponse,
  BTModifierGroupItemResponse,
  Button,
  Div,
  ErrorToast,
  H3,
  Icon,
  Img,
  P,
  PlusIcon,
  SuccessToast,
  addBrandTemplateMenuItem,
  deleteBrandTemplateMenuTypeItemById,
  noItems,
  updateBrandTemplateMenuItem,
  reportError,
} from '@vgrubs/components';
import { updateBrandTemplateMenuItemOrder } from 'components/src/api/brandTemplates.api';
import { ItemType } from 'components/src/types/menu.types';
import { UpdateBTMenuListOrdersDto } from 'components/src/types/brand.types';
import { ResponseType } from 'components/src/types/shared.types';
import { Dispatch, SetStateAction, useState } from 'react';
import { useParams } from 'react-router';
import { AddNewModifierGroupModal } from '../Modifiers/AddNewModifierGroupModal';
import { AddItemModal } from './AddItemModal';
import { AddModifierGroupModal } from './AddModifierGroupModal';
import { DeleteItemModal } from './DeleteItemModal';
import { MenuItemTypeComponent } from './MenuItemComponents';
import { PausedItemModal } from './PausedItemModal';

export const Items = ({
  menu,
  setMenu,
  setLoading,
}: {
  menu: BTMenuResponse | null;
  setMenu: Dispatch<SetStateAction<BTMenuResponse | null>>;
  setLoading: Dispatch<SetStateAction<boolean>>;
}) => {
  const [addModal, setAddModal] = useState(false);
  const [editModal, setEditModal] = useState<{
    item: BTMenuItemResponse;
    category: BTCategoryResponse;
  } | null>(null);
  const [pausedModal, setPausedModal] = useState(false);
  const [addModifierGroupModal, setAddModifierGroupModal] = useState(false);
  const [modifierGroups, setModifierGroups] = useState<
    BTModifierGroupItemResponse[]
  >([]);
  const [deleteModal, setDeleteModal] = useState<{
    item: BTMenuItemResponse;
    category: BTCategoryResponse;
  } | null>(null);
  const [addNewModifierGroupModal, setAddNewModifierGroupModal] =
    useState(false);
  const [activeId, setActiveId] = useState<number | null>(null);
  const [activeCategoryId, setActiveCategoryId] = useState<number | null>(null);
  const [isSorting, setIsSorting] = useState<BTMenuResponse | null>(null);

  const { brandTemplateId } = useParams() as {
    brandTemplateId: string;
  };

  async function handleAddItem(
    image: string | Blob | File | null,
    name: string,
    description: string,
    fromPrice: number,
    toPrice: number,
    categoryId: string,
    modifierGroups: BTModifierGroupItemResponse[] | null,
    preparationNotes: string,
    emptyForm: () => void
  ) {
    const body = {
      name,
      description,
      fromPrice,
      toPrice,
      categoryId,
      modifierGroupIds: modifierGroups?.map((item) => item.id),
      preparationNotes,
    };

    if (!name) return ErrorToast('Please enter item name');

    const formData = new FormData();
    if (typeof image !== 'string' && image !== null) {
      formData.append('image', image);
    }
    formData.append('menuItem', JSON.stringify(body));

    try {
      setLoading(true);
      const { data } = await addBrandTemplateMenuItem(
        brandTemplateId,
        formData
      );

      emptyForm();
      setMenu(data.data);
      SuccessToast(data.message);
      setAddModal(false);
    } catch (error) {
      reportError(error);
    } finally {
      setLoading(false);
    }
  }

  async function handleEditItem(
    image: string | Blob | File | null,
    name: string,
    description: string,
    fromPrice: number,
    toPrice: number,
    categoryId: string,
    modifierGroups: BTModifierGroupItemResponse[] | null,
    existingItem: BTMenuItemResponse,
    preparationNotes: string,
    emptyForm: () => void,
    removeImage: boolean | null
  ) {
    const body = {
      name,
      description,
      fromPrice,
      toPrice,
      categoryId,
      modifierGroupIds: modifierGroups?.map((item) => item.id),
      removeImage: removeImage ? removeImage : null,
      preparationNotes,
    };

    if (!name) return ErrorToast('Please enter item name');

    const formData = new FormData();
    if (typeof image !== 'string' && image !== null) {
      formData.append('image', image);
    }
    formData.append('menuItem', JSON.stringify(body));

    try {
      setLoading(true);
      const { data } = await updateBrandTemplateMenuItem(
        brandTemplateId,
        existingItem.id,
        formData
      );
      emptyForm();
      setMenu(data.data);
      SuccessToast(data.message);
      setEditModal(null);
    } catch (error) {
      reportError(error);
    } finally {
      setLoading(false);
    }
  }

  async function handleDeleteItem() {
    if (!deleteModal) return;
    try {
      setLoading(true);
      const { data } = await deleteBrandTemplateMenuTypeItemById(
        brandTemplateId,
        deleteModal.item.id,
        ItemType.Item
      );

      setMenu(data.data);
      SuccessToast(data.message);
      setDeleteModal(null);
    } catch (error) {
      reportError(error);
    } finally {
      setLoading(false);
    }
  }

  const AddItemButton = () => {
    return (
      <Button $selected onClick={() => setAddModal(true)}>
        Add Item
      </Button>
    );
  };

  function dragEndEvent(e: any, categoryId: number) {
    const { over, active } = e;
    moveItem({ over, active, categoryId });
  }

  const moveItem = ({
    over,
    active,
    categoryId,
  }: {
    over: { id: number };
    active: { id: number };
    categoryId: number;
  }) => {
    const category = menu?.categories?.find((cat) => cat.id === categoryId);
    if (!category?.items) return;

    const sortedItems = arrayMove(
      category.items,
      category.items.findIndex((item) => item.id === active.id),
      category.items.findIndex((item) => item.id === over?.id)
    );

    setMenu((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        categories: prev.categories.map((cat) =>
          cat.id === categoryId ? { ...cat, items: sortedItems } : cat
        ),
      };
    });
  };

  function handleDragStart(event: any, categoryId: number) {
    setActiveId(event.active.id);
    setActiveCategoryId(categoryId);
  }

  function handleDragEnd(event: any, categoryId: number) {
    if (event.over) {
      setActiveId(null);
      setActiveCategoryId(null);
      dragEndEvent(event, categoryId);
    }
  }

  async function handleSaveOrder() {
    try {
      setLoading(true);
      if (!menu?.categories) return;

      let lastData: ResponseType<BTMenuResponse> | null = null;
      for (const category of menu.categories) {
        if (category.items && category.items.length > 0) {
          const list: UpdateBTMenuListOrdersDto[] = category.items.map(
            (item, index) => ({
              id: item.id,
              order: index + 1,
            })
          );
          const { data } = await updateBrandTemplateMenuItemOrder(
            brandTemplateId,
            category.id,
            list
          );
          lastData = data;
        }
      }

      if (lastData) {
        setMenu(lastData.data);
        SuccessToast(lastData.message);
      }
      setIsSorting(null);
      setLoading(false);
    } catch (error) {
      reportError(error);
    } finally {
      setLoading(false);
    }
  }

  const totalChildrenCount = menu?.categories?.reduce((count, category) => {
    return count + (category.items ? category.items.length : 0);
  }, 0);

  const SortingButtons = () => {
    return (
      <>
        {isSorting && (
          <Button
            $selected
            $small
            $gray
            $w="130px"
            $wSM="calc(50% - 8px)"
            onClick={() => {
              setMenu(isSorting);
              setIsSorting(null);
            }}
          >
            Cancel Sorting
          </Button>
        )}
        <Button
          $selected={!!isSorting}
          $small
          $w="130px"
          $wSM={!isSorting ? '100%' : 'calc(50% - 8px)'}
          onClick={() => {
            if (!!isSorting) {
              return handleSaveOrder();
            }
            setIsSorting(menu);
          }}
        >
          {isSorting ? 'Save Sorting' : 'Sort Items'}
        </Button>
      </>
    );
  };

  return (
    <Div>
      <Div $flex $between $alignCenter $mb="20">
        <H3 $mb="0">Items {menu?.categories && `(${totalChildrenCount})`}</H3>
        <Div $flex $alignCenter $gap="12px">
          <Div $flex $gap="16px" $desktop>
            <SortingButtons />
          </Div>
          {menu?.categories && menu?.categories?.length > 0 && (
            <Icon $pointer onClick={() => setAddModal(true)}>
              <PlusIcon />
            </Icon>
          )}
        </Div>
      </Div>

      <Div $flex $gap="16px" $mobile $mb="20">
        <SortingButtons />
      </Div>

      <div>
        {menu?.categories?.map((item, i) => (
          <MenuItemTypeComponent
            key={item.id}
            item={item}
            onClick={(item) => setEditModal(item)}
            onClickDelete={(id) => setDeleteModal(id)}
            menu={menu}
            isSorting={!!isSorting}
            activeId={activeId}
            activeCategoryId={activeCategoryId}
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
          />
        ))}
      </div>

      {menu?.categories && menu?.categories?.length < 1 && (
        <Div $flex $column $alignCenter>
          <Img alt="" $width="180px" src={noItems} $mb="24" />
          <H3 $mb="24" $center>
            No Items
          </H3>
          <P $mb="32" $center>
            This brand template currently don’t have <br /> menu items.
          </P>
          <AddItemButton />
        </Div>
      )}

      <AddItemModal
        menu={menu}
        addModal={addModal}
        closeModal={() => {
          setAddModal(false);
        }}
        setAddModifierGroupModal={setAddModifierGroupModal}
        setModifierGroups={setModifierGroups}
        modifierGroups={modifierGroups}
        handleAddItem={handleAddItem}
      />

      <AddItemModal
        menu={menu}
        addModal={editModal}
        closeModal={() => {
          setEditModal(null);
        }}
        setAddModifierGroupModal={setAddModifierGroupModal}
        setModifierGroups={setModifierGroups}
        modifierGroups={modifierGroups}
        handleEditItem={handleEditItem}
        edit
      />

      <PausedItemModal
        pausedModal={pausedModal}
        closeModal={() => {
          setPausedModal(false);
        }}
      />
      <AddModifierGroupModal
        addModifierGroupModal={addModifierGroupModal}
        closeModal={() => {
          setAddModifierGroupModal(false);
        }}
        menu={menu}
        setModifierGroups={setModifierGroups}
        modifierGroups={modifierGroups}
        setAddNewModifierGroupModal={setAddNewModifierGroupModal}
      />

      <DeleteItemModal
        deleteModal={!!deleteModal}
        closeModal={() => {
          setDeleteModal(null);
        }}
        handleDeleteItem={handleDeleteItem}
      />

      <AddNewModifierGroupModal
        addNewModal={addNewModifierGroupModal}
        closeModal={() => {
          setAddNewModifierGroupModal(false);
        }}
        setMenu={setMenu}
        setLoading={setLoading}
      />
    </Div>
  );
};
